import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { Check, AlertCircle, Clock } from 'lucide-react';
import { functions } from '../utils/firebase';
import { useBooking } from '../hooks/useBooking';

type SessionStatus = 'open' | 'complete' | 'expired';
type PaymentStatus = 'paid' | 'unpaid' | 'no_payment_required';

interface CheckoutStatus {
  status: SessionStatus;
  paymentStatus: PaymentStatus;
  bookingId: string | null;
}

const POLL_INTERVAL_MS = 2000;
const POLL_MAX_ATTEMPTS = 8; // ~16s

const getCheckoutStatus = httpsCallable<unknown, CheckoutStatus>(
  functions,
  'stripeGetCheckoutSessionStatus',
);

export function ReturnPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { businessListing, resetBooking } = useBooking();

  const sessionId = searchParams.get('session_id');
  const listingId = businessListing?.id ?? null;

  const [state, setState] = useState<'loading' | 'success' | 'expired' | 'failed' | 'pending'>('loading');
  const [email, setEmail] = useState<string | null>(null);
  const pollAttemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId || !listingId) return;
    checkStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, listingId]);

  const checkStatus = async () => {
    if (!sessionId || !listingId) return;
    try {
      const result = await getCheckoutStatus({ listingId, sessionId });
      const { status, paymentStatus } = result.data;

      if (paymentStatus === 'paid') {
        setState('success');
        resetBooking();
        return;
      }

      if (status === 'expired') {
        setState('expired');
        return;
      }

      if (status === 'complete' && paymentStatus !== 'paid') {
        setState('failed');
        return;
      }

      // Session still open — poll
      pollAttemptsRef.current += 1;
      if (pollAttemptsRef.current >= POLL_MAX_ATTEMPTS) {
        setState('pending');
        return;
      }
      setState('pending');
      setTimeout(checkStatus, POLL_INTERVAL_MS);
    } catch (err) {
      console.error('[ReturnPage] checkStatus failed', err);
      setState('pending'); // Fall back to optimistic "confirming" message
    }
  };

  if (!sessionId) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-site-muted">Invalid return URL.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-site-bg text-site-text flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        {state === 'loading' && (
          <div className="bg-site-bg rounded-2xl border border-site-border p-8 text-center space-y-4">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 mx-auto" style={{ borderColor: 'var(--site-primary)' }} />
            <p className="text-site-muted">Checking payment status…</p>
          </div>
        )}

        {state === 'pending' && (
          <div className="bg-site-bg rounded-2xl border border-site-border p-8 text-center space-y-4">
            <Clock className="w-12 h-12 mx-auto text-site-warning" />
            <h1 className="text-xl font-bold text-site-text">Confirming your booking…</h1>
            <p className="text-site-muted text-sm">
              {pollAttemptsRef.current >= POLL_MAX_ATTEMPTS
                ? "We're still confirming your booking. You'll receive a confirmation email shortly."
                : 'Just a moment while we confirm your payment.'}
            </p>
            {pollAttemptsRef.current < POLL_MAX_ATTEMPTS && (
              <div className="animate-pulse flex justify-center gap-1 pt-2">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="w-2 h-2 rounded-full bg-site-muted" />
                ))}
              </div>
            )}
          </div>
        )}

        {state === 'success' && (
          <div className="bg-site-bg rounded-2xl border border-site-border p-8 text-center space-y-4">
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center mx-auto"
              style={{ backgroundColor: 'var(--site-primary)', border: '1px solid var(--site-primary-outline)' }}
            >
              <Check className="w-8 h-8 text-site-on-primary" />
            </div>
            <h1 className="text-2xl font-bold text-site-text">Booking confirmed!</h1>
            {email && (
              <p className="text-site-muted text-sm">
                A confirmation has been sent to <strong>{email}</strong>.
              </p>
            )}
            <p className="text-site-muted text-sm">
              Your deposit has been received. You'll pay the remaining balance at your appointment.
            </p>
            <Link
              to="/"
              className="block mt-4 text-sm font-medium underline"
              style={{ color: 'var(--site-link)' }}
            >
              Back to home
            </Link>
          </div>
        )}

        {state === 'expired' && (
          <div className="bg-site-bg rounded-2xl border border-site-border p-8 text-center space-y-4">
            <AlertCircle className="w-12 h-12 mx-auto text-site-muted" />
            <h1 className="text-xl font-bold text-site-text">Session expired</h1>
            <p className="text-site-muted text-sm">
              Your payment session expired. The slot is no longer held — please pick a time again.
            </p>
            <Link
              to="/book"
              className="inline-block mt-2 text-sm font-semibold py-2.5 px-6 rounded-full text-site-on-primary"
              style={{ backgroundColor: 'var(--site-primary)', border: '1px solid var(--site-primary-outline)' }}
            >
              Start over
            </Link>
          </div>
        )}

        {state === 'failed' && (
          <div className="bg-site-bg rounded-2xl border border-site-border p-8 text-center space-y-4">
            <AlertCircle className="w-12 h-12 mx-auto text-site-danger" />
            <h1 className="text-xl font-bold text-site-text">Payment failed</h1>
            <p className="text-site-muted text-sm">
              Your card wasn't charged. Please try again or pick a different time.
            </p>
            <Link
              to="/book"
              className="inline-block mt-2 text-sm font-semibold py-2.5 px-6 rounded-full text-site-on-primary"
              style={{ backgroundColor: 'var(--site-primary)', border: '1px solid var(--site-primary-outline)' }}
            >
              Try again
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
